<script lang="ts" setup>
const emit = defineEmits(["close"]);
const route = useRoute();
const props = defineProps({
  existingLink: {
    type: Boolean,
    default: false,
  },
  support: {
    type: Boolean,
    default: false,
  },
});
const { updatePasswordWalletKey, updateWalletKeys } = useUserController();
const userStore = useUserStore();
const { toaster } = useToaster();
const showWalletSecurityNotice = ref(false);
const showWalletRecoveryChoice = ref(false);
const showWalletRecoveryPassword = ref(false);
const showWalletSupport = ref(false);
const showWalletUnlocked = ref(false);
const isReturningUser = ref(useUserStore().accountAddress !== null);
const isLoading = ref(false);
const password = ref<string | null>(null);
const localePath = useLocalePath();

const handleContinueUpdate = async () => {
  if (props.support === true || !(await hasAvailableRecoveryMethods())) {
    showWalletSecurityNotice.value = false;
    showWalletSupport.value = true;
    showWalletRecoveryPassword.value = false;
  } else {
    showWalletSecurityNotice.value = false;
    showWalletRecoveryChoice.value = true;
    showWalletRecoveryPassword.value = false;
  }
};

const handleUseRecoveryPassword = () => {
  showWalletSecurityNotice.value = false;
  showWalletRecoveryChoice.value = false;
  showWalletRecoveryPassword.value = true;
};

const handleSupport = () => {
  showWalletRecoveryChoice.value = false;
  showWalletSupport.value = true;
  showWalletRecoveryPassword.value = false;
};

const handleBackToRecoveryChoice = () => {
  if (props.support === true) {
    showWalletSecurityNotice.value = true;
    showWalletSupport.value = false;
    showWalletRecoveryChoice.value = false;
    showWalletRecoveryPassword.value = false;
  } else {
    showWalletSupport.value = false;
    showWalletRecoveryChoice.value = true;
    showWalletRecoveryPassword.value = false;
  }
};

const handleSuccess = () => {
  showWalletUnlocked.value = true;
  showWalletSecurityNotice.value = false;
  showWalletRecoveryPassword.value = false;
};

const handleLoading = () => {
  showWalletSupport.value = false;
  showWalletRecoveryChoice.value = false;
  showWalletRecoveryPassword.value = false;
  isLoading.value = true;
};

const hasAvailableRecoveryMethods = async () => {
  try {
    const gfalid = useCookie(`gfalid-${useUserStore().id}`);
    if (!gfalid.value) throw new Error("Missing secret");
    const parsedSecret = await parseSecret(gfalid.value);
    if (!parsedSecret) throw new Error("Unable to parse secret");
    const { google, apple, facebook, recoveryPassword } = userStore.keys || {};
    return (
      (google != null && parsedSecret.type !== "google") ||
      (apple != null && parsedSecret.type !== "apple") ||
      (facebook != null && parsedSecret.type !== "facebook") ||
      (recoveryPassword != null && parsedSecret.type !== "recoveryPassword")
    );
  } catch (error) {
    console.error("Error checking available recovery methods", error);
  }
};

const updateKeysFromRecovery = async () => {
  const gfalid = useCookie(`gfalid-${useUserStore().id}`);
  if (!gfalid.value) throw new Error("Missing secret");
  const parsedSecret = await parseSecret(gfalid.value);
  if (!parsedSecret) throw new Error("Unable to parse secret");
  if (parsedSecret.type === "password") {
    password.value = parsedSecret.value;
    await updatePasswordKey(password.value!);
  } else if (
    parsedSecret.type === "google" ||
    parsedSecret.type === "apple" ||
    parsedSecret.type === "facebook"
  ) {
    await updateSocialKey(parsedSecret.value, parsedSecret.type);
  } else {
    throw new Error("Invalid secret type");
  }
};

const updateKeyFromSocial = async () => {
  const firstSecret = useCookie(`gfalid-${useUserStore().id}`);
  const secondSecret = useCookie(`link-${useUserStore().id}`);
  if (!firstSecret.value || !secondSecret.value)
    throw new Error("Missing secret");
  const passwordSecret = await parseSecret(firstSecret.value);
  const linkedSocialSecret = await parseSecret(secondSecret.value);
  if (!passwordSecret || !linkedSocialSecret) throw new Error("Missing secret");
  password.value = passwordSecret.value;
  const privateKey = await getPrivateKeyFromSocial(
    linkedSocialSecret.value,
    linkedSocialSecret.type
  );
  await updatePasswordKey(password.value!, privateKey);
};

const updatePasswordKey = async (password: string, privateKey?: string) => {
  try {
    handleLoading();
    await updatePasswordWalletKey(password, privateKey);
    isLoading.value = false;
    userStore.walletLocked = false;
    handleSuccess();
  } catch (error) {
    console.error("Error updating key", error);
    handleErrorUnlockingWallet();
  }
};

const updateSocialKey = async (socialSecret: string, type: string) => {
  try {
    handleLoading();
    await updateWalletKeys({
      method: type as UserKeyMethod,
      value: socialSecret,
      password: null,
    });
    isLoading.value = false;
    userStore.walletLocked = false;
    handleSuccess();
  } catch (error: any) {
    console.error("Error updating social key", error);
    handleErrorUnlockingWallet();
  }
};

const handlePasswordType = async () => {
  const gfalid = useCookie(`gfalid-${useUserStore().id}`);
  if (!gfalid.value) throw new Error("Missing secret");
  const parsedSecret = await parseSecret(gfalid.value);
  if (!parsedSecret) throw new Error("Unable to parse secret");
  if (parsedSecret.type === "password") {
    password.value = parsedSecret.value;
    showWalletSecurityNotice.value = true;
  } else if (
    parsedSecret.type === "google" ||
    parsedSecret.type === "apple" ||
    parsedSecret.type === "facebook"
  ) {
    showWalletSecurityNotice.value = true;
  } else {
    throw new Error("Invalid secret type");
  }
};

const getPrivateKeyFromSocial = async (socialSecret: string, type: string) => {
  try {
    if (
      (type == "google" || type == "apple" || type == "facebook") &&
      userStore.keys?.[type]?.value
    ) {
      const privateKey = await decrypt(
        userStore.keys[type].value,
        socialSecret
      );
      useWeb3().setPrivateKey(privateKey);
      return privateKey;
    } else {
      throw new Error("No social key found");
    }
  } catch (error: any) {
    console.error("Error getting private key from social", error);
    handleErrorUnlockingWallet();
  }
};

const modalWidth = computed(() => {
  if (
    showWalletSecurityNotice.value ||
    showWalletUnlocked.value ||
    isLoading.value
  )
    return "medium";
  if (showWalletRecoveryChoice.value || showWalletSupport.value) return "xl";
  return "xl";
});

const handleErrorUnlockingWallet = () => {
  console.error("Error unlocking wallet");
  toaster("error");
  handleBackToRecoveryChoice();
};

const handleBackToSecurityNotice = () => {
  showWalletSecurityNotice.value = true;
  showWalletSupport.value = false;
  showWalletRecoveryChoice.value = false;
  showWalletRecoveryPassword.value = false;
};

onMounted(async () => {
  try {
    if (route.query["existing-link"]) updateKeyFromSocial();
    else await handlePasswordType();
  } catch (error) {
    console.error("Error unlocking wallet", error);
    navigateTo(localePath("/?cause=error"));
  }
});

watch(
  () => userStore.isLoggedIn,
  (newVal) => {
    if (!newVal) useModalManager().closeModal("modal-user-wallet-locked");
  },
  { immediate: true }
);

const toggleHideX = () => {
  if (showWalletRecoveryChoice.value) return false;
  return true;
};
</script>

<template>
  <BaseLayoutModal
    :width="modalWidth"
    :hide-x="toggleHideX()"
    @close="handleBackToSecurityNotice">
    <template #header>
      {{ $t("main.walletLock.walletLocked") }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalUserWalletLockedSecurityNotice
          v-if="showWalletSecurityNotice"
          @continue="handleContinueUpdate"
          @close="emit('close')" />
        <ModalUserWalletLockedRecoveryChoice
          v-else-if="showWalletRecoveryChoice"
          @continue="updateKeyFromSocial"
          @skip="handleUseRecoveryPassword"
          @back="handleBackToSecurityNotice"
          @to-support="handleSupport" />
        <ModalUserWalletLockedRecoveryPassword
          v-else-if="showWalletRecoveryPassword"
          @continue="updateKeysFromRecovery"
          @back="handleBackToRecoveryChoice" />
        <ModalUserWalletLockedSupport
          v-else-if="showWalletSupport"
          @back="handleBackToRecoveryChoice" />
        <ModalUserWalletLockedSuccess
          v-else-if="showWalletUnlocked"
          :is-returning-user="isReturningUser"
          @close="emit('close')" />
        <ModalLoader
          v-else-if="isLoading"
          :body-title="$t('main.modalUserWalletCreate.returningWallet')"
          :body-subtitle="
            $t('main.modalUserWalletCreate.returningWalletSubtitle')
          " />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>

<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
